import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper } from "@mui/material";
import type {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { SawQualityCheckRow } from "../../repositories/sawQualityChecksReportRepository";
import { useTableData } from "../hooks/useTableData";

type Props = {
	isLoading: boolean;
	rows: SawQualityCheckRow[];
	setGridApi: (api: GridApi) => void;
	onReclassify: (check: SawQualityCheckRow) => void;
	onEdit: (check: SawQualityCheckRow) => void;
	onDelete: (check: SawQualityCheckRow) => void;
};

export const SawQualityChecksReportTable = ({
	isLoading,
	rows,
	setGridApi,
	onReclassify,
	onEdit,
	onDelete,
}: Props) => {
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		onReclassify,
		onEdit,
		onDelete,
	});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "sawQualityChecksContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[showContextMenu],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: register once; the
	// handlers behind getMenuOptions are stable callbacks from the page.
	useEffect(() => {
		registerConfig({
			id: "sawQualityChecksContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2
			container
			size={{ md: 12, xs: 12 }}
			sx={{ height: "65vh", minHeight: "65vh" }}
			component={Paper}
			elevation={2}
		>
			<Grid2 size={{ md: 12, xs: 12 }} height={"100%"}>
				<AgGridReact<SawQualityCheckRow>
					loading={isLoading}
					animateRows
					rowData={rows}
					columnDefs={columnDefs}
					tooltipShowDelay={300}
					getContextMenuItems={getContextMenuItems}
					onRowDoubleClicked={(event) => event.data && onEdit(event.data)}
					getRowId={(params) => `${params.data.QualityCheckId}`}
					onGridReady={(params) => setGridApi(params.api)}
					loadingOverlayComponent={CenteredLazyLoading}
					gridOptions={{
						rowSelection: {
							mode: "singleRow",
							checkboxes: false,
							enableClickSelection: true,
						},
					}}
				/>
			</Grid2>
		</Grid2>
	);
};
